import ApplicationController from './application_controller';
import log from '../lib/logger';

export default class extends ApplicationController {
  static targets = ['universeChart', 'playButton', 'pauseButton', 'resetButton', 'currentMonth'];

  connect() {
    log.info('Revenue universe controller connected');
    this.revenueUniverseInstance = null;
    this.initializationPromise = null;

    // Initialize the universe chart when the controller connects
    if (this.hasUniverseChartTarget) {
      this.initializeUniverseChart();
    }
  }

  universeChartTargetConnected() {
    log.info('Universe chart target connected');
    // This will be called when the universe chart element is added to the DOM
    this.initializeUniverseChart();
  }

  initializeUniverseChart() {
    // Prevent double initialization
    if (this.initializationPromise) {
      log.info('Revenue universe initialization already in progress');
      return this.initializationPromise;
    }

    // Create and store the initialization promise
    this.initializationPromise = import('../pages/revenue_universe')
      .then((module) => {
        log.info('Revenue universe module loaded');
        const revenueUniverse = module.default;

        if (typeof revenueUniverse === 'function') {
          // Add a timeout to ensure DOM is ready
          return new Promise((resolve, reject) => {
            setTimeout(() => {
              try {
                // Create the instance
                this.revenueUniverseInstance = revenueUniverse();
                log.info('Revenue universe instance created:', this.revenueUniverseInstance);

                // Now manually initialize the chart
                if (this.revenueUniverseInstance && this.revenueUniverseInstance.init) {
                  const initialized = this.revenueUniverseInstance.init();

                  if (initialized) {
                    log.info('Revenue universe successfully initialized');
                    resolve(this.revenueUniverseInstance);
                  } else {
                    log.error('Revenue universe failed to initialize');
                    reject(new Error('Chart initialization failed'));
                  }
                } else {
                  log.error('Revenue universe instance missing init method');
                  reject(new Error('Invalid module structure'));
                }
              } catch (error) {
                log.error('Error creating revenue universe instance:', error);
                reject(error);
              }
            }, 100); // Delay to ensure DOM is ready
          });
        } else {
          log.error('Revenue universe module did not export a function');
          throw new Error('Invalid module export');
        }
      })
      .catch((error) => {
        log.error('Error loading revenue universe module:', error);
        this.initializationPromise = null; // Reset so it can be retried
        throw error;
      });

    return this.initializationPromise;
  }

  async playAnimation() {
    log.info('Play animation button clicked');

    try {
      // Wait for initialization if needed
      if (!this.revenueUniverseInstance) {
        await this.initializationPromise;
      }

      if (this.revenueUniverseInstance && this.revenueUniverseInstance.playAnimation) {
        const isPlaying = this.revenueUniverseInstance.playAnimation();

        // Toggle button visibility based on animation state
        if (isPlaying) {
          // Animation started/resumed
          if (this.hasPlayButtonTarget) this.playButtonTarget.style.display = 'none';
          if (this.hasPauseButtonTarget) this.pauseButtonTarget.style.display = 'inline-block';
        } else {
          // Animation was paused (this shouldn't happen from play button, but just in case)
          if (this.hasPlayButtonTarget) this.playButtonTarget.style.display = 'inline-block';
          if (this.hasPauseButtonTarget) this.pauseButtonTarget.style.display = 'none';
        }
      } else {
        log.error('Revenue universe instance not ready or playAnimation method not available');
      }
    } catch (error) {
      log.error('Error playing animation:', error);
    }
  }

  async pauseAnimation() {
    log.info('Pause animation button clicked');

    try {
      // Wait for initialization if needed
      if (!this.revenueUniverseInstance) {
        await this.initializationPromise;
      }

      if (this.revenueUniverseInstance && this.revenueUniverseInstance.pauseAnimation) {
        this.revenueUniverseInstance.pauseAnimation();

        // Toggle button visibility
        if (this.hasPlayButtonTarget) this.playButtonTarget.style.display = 'inline-block';
        if (this.hasPauseButtonTarget) this.pauseButtonTarget.style.display = 'none';
      } else {
        log.error('Revenue universe instance not ready or pauseAnimation method not available');
      }
    } catch (error) {
      log.error('Error pausing animation:', error);
    }
  }

  async resetView() {
    log.info('Reset view button clicked');

    try {
      // Wait for initialization if needed
      if (!this.revenueUniverseInstance) {
        await this.initializationPromise;
      }

      if (this.revenueUniverseInstance && this.revenueUniverseInstance.resetView) {
        this.revenueUniverseInstance.resetView();

        // Reset button visibility
        if (this.hasPlayButtonTarget) this.playButtonTarget.style.display = 'inline-block';
        if (this.hasPauseButtonTarget) this.pauseButtonTarget.style.display = 'none';
      } else {
        log.error('Revenue universe instance not ready or resetView method not available');
      }
    } catch (error) {
      log.error('Error resetting view:', error);
    }
  }

  disconnect() {
    log.info('Revenue universe controller disconnecting');

    // Cleanup when controller disconnects
    if (this.revenueUniverseInstance && this.revenueUniverseInstance.destroy) {
      this.revenueUniverseInstance.destroy();
      this.revenueUniverseInstance = null;
      this.initializationPromise = null;
    }
  }
}
